import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { difference, differenceBy } from 'lodash';
import {
  ChevronDownIcon,
  MenuFuseHighlight,
  MenuSearchBox,
  Button,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  mapMixedOptions,
  MenuSearchFuseItem,
  Box,
} from '@cdl/heartbeat-react';
import { definitionsSelector } from '../selectors/diagnostic.selectors';
import { manualAbnormalitiesSelector } from '../selectors/user-diagnostic.selectors';
import { trackDiagnosticLabel } from '../../analytics/thunks/track-diagnostic-label.thunk';
import { saveManualAbnormalities } from '../thunks/save-manual-abnormalities.thunk';
import { useTypedAbnormalities } from '../hooks/useDefinitionsToAbnormalities';
import { Abnormality } from '../types/diagnostic';
import {
  ADD_DIAGNOSTIC_LABEL,
  DELETE_DIAGNOSTIC_LABEL,
} from '../../analytics/analytics.constants';
import { DiagnosticLabelList } from './DiagnosticLabelList';
import { annotationTableSelector } from '../../auth/selectors/auth.selectors';
import {
  ANNOTATION_TABLE_GROUPING,
  OTHER_CATEGORY,
} from '../../resting/resting.util';
import { useAbnormalityByCode } from '../hooks/useAbnormalityByCode';
import { hasAutoLabelsAndNoDelineationsSelector } from '../../delineation/selectors/delineation.selectors';
import { needToAssessSelector } from '../../autoLabels/selectors/auto-labels.selectors';

export const DiagnosticPicker: React.FC = () => {
  const dispatch = useDispatch();
  const hasAutoLabelsAndNoDelineation = useSelector(
    hasAutoLabelsAndNoDelineationsSelector,
  );
  const needToAssess = useSelector(needToAssessSelector);
  const isMenuDisabled = hasAutoLabelsAndNoDelineation || needToAssess;
  const userAnnotationTable = useSelector(annotationTableSelector);
  const pathGroupSettings =
    userAnnotationTable && ANNOTATION_TABLE_GROUPING[userAnnotationTable];
  const allAvailableDefinitions = useSelector(definitionsSelector);
  const typedAbnormalities = useTypedAbnormalities(allAvailableDefinitions);
  const storedAbnormalities = useSelector(manualAbnormalitiesSelector);
  const storedAbnormalityCodes = useMemo(() => {
    return storedAbnormalities.map((abnormality) => abnormality.code);
  }, [storedAbnormalities]);

  const options = useMemo(
    () =>
      typedAbnormalities.map((type) => ({
        value: type.code,
        displayValue: type.name,
      })),
    [typedAbnormalities],
  );

  const abnormalityByCode = useAbnormalityByCode();
  const { groupTitleByAbnormalityCode, abnormalityByGroupTitle } =
    useMemo(() => {
      const groupTitleByAbnormalityCode: Record<string, string> = {};
      const abnormalityByGroupTitle: Record<string, Abnormality[]> = {};

      typedAbnormalities.forEach((abnormality) => {
        if (pathGroupSettings && abnormality.category != null) {
          const groupTitle =
            pathGroupSettings.pathGroupMapping[abnormality.category] ||
            OTHER_CATEGORY;
          groupTitleByAbnormalityCode[abnormality.code] = groupTitle;

          if (abnormalityByGroupTitle[groupTitle] == null) {
            abnormalityByGroupTitle[groupTitle] = new Array(abnormality);
          } else {
            abnormalityByGroupTitle[groupTitle].push(abnormality);
          }
        }
      });
      return {
        groupTitleByAbnormalityCode,
        abnormalityByGroupTitle,
      };
    }, [pathGroupSettings, typedAbnormalities]);

  const menuItemOptions = useMemo(() => {
    const mixedOptions = pathGroupSettings
      ? pathGroupSettings.groupOrder.flatMap((groupTitle) => {
          if (abnormalityByGroupTitle[groupTitle] == null) {
            return [];
          }
          const optionsInGroup = abnormalityByGroupTitle[groupTitle].map(
            ({ code, name }) => ({
              value: code,
              displayValue: name,
            }),
          );
          return [{ title: groupTitle }, ...optionsInGroup];
        })
      : options;

    return mapMixedOptions(mixedOptions, ({ value, displayValue }) => (
      <MenuItemOption key={`option-no-result-${value}`} value={value}>
        {displayValue}
      </MenuItemOption>
    ));
  }, [pathGroupSettings, options, abnormalityByGroupTitle]);

  const getMenuItemByGroup = (filteredOptions: MenuSearchFuseItem[]) => {
    const filteredOptionsByGroup = filteredOptions.reduce(
      (acc: Record<string, MenuSearchFuseItem[]>, fuseItem) => {
        if (acc[groupTitleByAbnormalityCode[fuseItem.item.value]] == null) {
          acc[groupTitleByAbnormalityCode[fuseItem.item.value]] = [];
        }
        acc[groupTitleByAbnormalityCode[fuseItem.item.value]].push(fuseItem);
        return acc;
      },
      {},
    );
    // Compute mean score for each group
    const meanScoreByGroup = Object.entries(filteredOptionsByGroup).reduce(
      (acc: Record<string, number>, [groupTitle, options]) => {
        const scoreSum = options.reduce((sum, option) => sum + option.score, 0);
        acc[groupTitle] = scoreSum / options.length;
        return acc;
      },
      {},
    );
    // Sort groups by mean score ascending (lower score = close to perfect match)
    const sortedGroupTitles = Object.keys(meanScoreByGroup).sort((a, b) => {
      return meanScoreByGroup[a] - meanScoreByGroup[b];
    });

    // Build option array sorted by group mean score including group title
    const mixedOptions = sortedGroupTitles.flatMap((groupTitle) => {
      if (filteredOptionsByGroup[groupTitle] != null) {
        return [{ title: groupTitle }, ...filteredOptionsByGroup[groupTitle]];
      }
      return [];
    });

    return mapMixedOptions(mixedOptions, (fuseOption) => (
      <MenuItemOption
        key={`option-${fuseOption.item.value}`}
        value={fuseOption.item.value}
      >
        <MenuFuseHighlight fallback={fuseOption.item.value} hit={fuseOption} />
      </MenuItemOption>
    ));
  };

  const setAbnormalities = useCallback(
    (abnormalities: Abnormality[]) => {
      if (storedAbnormalities.length > abnormalities.length) {
        const labelName = differenceBy(
          storedAbnormalities,
          abnormalities,
          'code',
        )[0].name;
        dispatch(trackDiagnosticLabel(DELETE_DIAGNOSTIC_LABEL, labelName));
      } else {
        const labelName = differenceBy(
          abnormalities,
          storedAbnormalities,
          'code',
        )[0].name;
        // This fn is called only when we add or remove a label. So storedAbnormalities and
        // abnormalities will never have the same size inside this function
        dispatch(trackDiagnosticLabel(ADD_DIAGNOSTIC_LABEL, labelName));
      }
      dispatch(saveManualAbnormalities(abnormalities));
    },
    [dispatch, storedAbnormalities],
  );

  const handleChange = useCallback(
    (optionCodes: string | string[]) => {
      const selectedAbnormalityCodes = Array.isArray(optionCodes)
        ? optionCodes
        : [optionCodes];
      let selectedAbnormalities: Abnormality[] = [];
      if (storedAbnormalityCodes.length > selectedAbnormalityCodes.length) {
        // delete abnormality
        const deletedAbnormalityCode = difference(
          storedAbnormalityCodes,
          selectedAbnormalityCodes,
        )[0];
        selectedAbnormalities = storedAbnormalities.filter(
          ({ code }) => code !== deletedAbnormalityCode,
        );
      } else {
        // add abnormality
        const addedAbnormalityCode = difference(
          selectedAbnormalityCodes,
          storedAbnormalityCodes,
        )[0];
        selectedAbnormalities = storedAbnormalities.concat(
          abnormalityByCode[addedAbnormalityCode],
        );
      }
      setAbnormalities(selectedAbnormalities);
    },
    [
      setAbnormalities,
      abnormalityByCode,
      storedAbnormalityCodes,
      storedAbnormalities,
    ],
  );

  const deleteAbnormality = useCallback(
    (abnormality: Abnormality) => {
      const newAbnormalities = storedAbnormalities.filter(
        (storedAbnormality) => storedAbnormality.code !== abnormality.code,
      );
      setAbnormalities(newAbnormalities);
    },
    [storedAbnormalities, setAbnormalities],
  );

  return (
    <>
      <Box>
        <Menu closeOnSelect={false} matchWidth isLazy>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            variant='secondary'
            textAlign='left'
            fontWeight='regular'
            w='full'
            data-testid='diagnosis-dropdown'
            isDisabled={isMenuDisabled}
          >
            Additional diagnoses
          </MenuButton>
          <MenuList maxWidth='unset'>
            <MenuSearchBox
              placeholder='Search...'
              options={options}
              fuseOptions={{
                ignoreLocation: true,
                minMatchCharLength: 2,
                includeScore: true,
              }}
            >
              {(filteredOptions) => (
                <MenuOptionGroup
                  type='checkbox'
                  value={storedAbnormalityCodes}
                  onChange={handleChange}
                >
                  {filteredOptions.length > 0
                    ? pathGroupSettings
                      ? getMenuItemByGroup(filteredOptions)
                      : filteredOptions.map((fuseOption) => (
                          <MenuItemOption
                            key={`option-${fuseOption.item.value}`}
                            value={fuseOption.item.value}
                          >
                            <MenuFuseHighlight
                              fallback={fuseOption.item.value}
                              hit={fuseOption}
                            />
                          </MenuItemOption>
                        ))
                    : menuItemOptions}
                </MenuOptionGroup>
              )}
            </MenuSearchBox>
          </MenuList>
        </Menu>
      </Box>
      <DiagnosticLabelList deleteAbnormality={deleteAbnormality} />
    </>
  );
};
